import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, CircleCheck, CircleX, Flag } from 'lucide-react';
import InlineSpinner from '../../shared/InlineSpinner';

import { PortaledLayer } from '../../shared/PortaledLayer';
import RefreshButton from '../../shared/RefreshButton';
import { DetailCard } from '../../Device/details/DetailCard';
import { DeviceStatPills, type DeviceStatPill } from '../../Device/details/DeviceStatPills';
import EventAssignedDevicesTable from './EventAssignedDevicesTable';
import DevicePackageAssignmentPortal from './DevicePackageAssignmentPortal';
import { RaceConfigurationCard } from './RaceConfigurationCard';
import { useAthleteRosterCard } from './AthleteRosterCard';
import { formatStatusLabel, getRaceStatusBadgeClass, isCombinedModeRace } from '../utils';
import { isPackagingToDistributionFullyReady } from '../utils/racePackagingToDistributionReadiness';
import type { PackagingToDistributionReadinessCheck } from '../types';
import DeviceCommandsGateDialog from './DeviceCommandsGateDialog';
import DistributionMasterTable from './DistributionMasterTable';
import {
  areAllPackagingCommandStepsComplete,
  emptyPackagingCommandChecklist,
  readPackagingCommandChecklist,
  togglePackagingCommandStep,
  writePackagingCommandChecklist,
  type PackagingCommandStepKey,
} from '../utils/raceDeviceCommandChecklist';
import {
  countAssignedRaceDevices,
  countCompleteRaceAthleteDevicePairs,
  countRetrievedRaceDevices,
  raceStatusShowsRetrieval,
} from '../utils/raceRetrievalCounts';
import type { RaceDetailActionItem, RaceDetailPortalModel } from '../types/RaceDetailPortalTypes';
import RaceReportingPanel from './RaceReportingPanel';
import {
  getRaceValidationReviewOutcome,
  isRaceOverviewStatRelevant,
  isRaceStackedOverlayOpen,
  markRaceStackedOverlayJustClosed,
  raceOverviewStatUnavailableHint,
  shouldSuppressRacePortalBackdropClose,
} from '../utils/raceDetailPortalHelpers';

function isReadinessFetchError(
  v: PackagingToDistributionReadinessCheck[] | { error: string } | undefined,
): v is { error: string } {
  return !!v && typeof v === 'object' && 'error' in v && typeof (v as { error: string }).error === 'string';
}

type Props = {
  open: boolean;
  model: RaceDetailPortalModel | null;
  eventId: string;
  isDetailsLoaded: boolean;
  isRefreshingEventDetails: boolean;
  statusReloadKey: number;
  isAdmin: boolean;
  globalUserIdToDisplayName: Record<string, string>;
  onClose: () => void;
  onRefresh?: (raceId?: string) => void | Promise<void>;
};

function actionToneClass(tone: RaceDetailActionItem['tone']): string {
  const base = 'rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition-all hover:-translate-y-[1px] hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0';
  switch (tone) {
    case 'danger':
      return `${base} border-red-300 bg-red-50 text-red-900 hover:bg-red-100 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-100`;
    case 'warn':
      return `${base} border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100`;
    case 'primary':
      return `${base} border-blue-300 bg-blue-50 text-blue-900 hover:bg-blue-100 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-100`;
    default:
      return `${base} border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800`;
  }
}

function strongPulseClass(): string {
  return [
    'animate-pulse',
    '!border-blue-600 !bg-blue-600 !text-white dark:!border-blue-500 dark:!bg-blue-500 dark:!text-white',
    'ring-2 ring-blue-300/90 dark:ring-blue-500/80',
    'ring-offset-1 ring-offset-white dark:ring-offset-slate-900',
    'shadow-[0_0_0_4px_rgba(59,130,246,0.24)] dark:shadow-[0_0_0_4px_rgba(59,130,246,0.38)]',
    'hover:!bg-blue-700 dark:hover:!bg-blue-400',
  ].join(' ');
}

function stageNavButtonClass(direction: 'backward' | 'forward', pulse = false): string {
  const base = [
    'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border-2 px-4 text-[11px] font-bold uppercase tracking-wide transition-all',
    'hover:-translate-y-[1px] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:translate-y-0 disabled:hover:shadow-none',
    'ring-offset-2 ring-offset-white dark:ring-offset-slate-900',
  ].join(' ');
  if (pulse) {
    return `${base} ${strongPulseClass()}`;
  }
  if (direction === 'backward') {
    return [
      base,
      'border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50',
      'dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800',
    ].join(' ');
  }
  return [
    base,
    'border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700',
    'dark:border-blue-500 dark:bg-blue-600 dark:hover:bg-blue-500',
  ].join(' ');
}

type ValidationCtaTone = 'pending' | 'pass' | 'fail' | 'reviewed';

function validationCtaButtonClass(tone: ValidationCtaTone, pulse: boolean): string {
  const base = 'inline-flex min-w-[14rem] items-center justify-center gap-2 rounded-xl border-2 px-8 py-3 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-45';
  const toneClass: Record<ValidationCtaTone, string> = {
    pending:
      'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-950/60',
    pass:
      'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-950/60',
    fail:
      'border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-100 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-100 dark:hover:bg-rose-950/60',
    reviewed:
      'border-slate-300 bg-slate-50 text-slate-800 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-100 dark:hover:bg-slate-800',
  };
  if (pulse) {
    return [
      base,
      'animate-pulse !border-amber-500 !bg-amber-500 !text-white',
      'ring-2 ring-amber-300/90 ring-offset-2 ring-offset-white dark:ring-offset-slate-900',
      'shadow-[0_0_0_4px_rgba(245,158,11,0.28)] hover:!bg-amber-600',
    ].join(' ');
  }
  return [base, toneClass[tone]].join(' ');
}

function stageStatusBannerClass(status: string): string {
  const st = String(status ?? '').toUpperCase();
  if (st === 'ACTIVE') {
    return 'border-emerald-300 bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-sm shadow-emerald-500/20 dark:border-emerald-500 dark:from-emerald-600 dark:to-green-600';
  }
  if (st === 'COMPLETE') {
    return 'border-violet-300 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-sm shadow-violet-500/20 dark:border-violet-500 dark:from-violet-600 dark:to-fuchsia-600';
  }
  if (st === 'RACKING') {
    return 'border-cyan-300 bg-cyan-100 text-cyan-900 dark:border-cyan-700/60 dark:bg-cyan-950/50 dark:text-cyan-100';
  }
  if (st === 'DISTRIBUTION') {
    return 'border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-700/60 dark:bg-sky-950/50 dark:text-sky-100';
  }
  if (st === 'PACKAGING') {
    return 'border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/50 dark:text-amber-100';
  }
  return 'border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-100';
}

function stageStatusEyebrow(status: string): string {
  const st = String(status ?? '').toUpperCase();
  if (st === 'ACTIVE') return 'Live now';
  if (st === 'REPORTING') return 'Post-race';
  if (st === 'COMPLETE') return 'Finished';
  return 'Current stage';
}

function compactActionButton(
  action: RaceDetailActionItem,
  label?: string,
  options?: { pulse?: boolean },
): ReactElement {
  return (
    <button
      type="button"
      disabled={action.disabled}
      title={action.title ?? action.description}
      className={`${actionToneClass(action.tone)} ${options?.pulse ? strongPulseClass() : ''}`}
      onClick={action.onClick}
    >
      {label ?? action.label}
    </button>
  );
}

function stageTargetFromAction(action: RaceDetailActionItem | undefined, direction: 'forward' | 'backward'): string {
  if (!action?.label) return '';
  const label = action.label.trim();
  if (direction === 'forward') {
    return label.replace(/^Move forward to\s*/i, '').replace(/^Move to\s*/i, '') || label;
  }
  return label.replace(/^Move backward to\s*/i, '').replace(/^Move to\s*/i, '') || label;
}

function stageDescription(status: string, model: RaceDetailPortalModel): string {
  switch (String(status ?? '').toUpperCase()) {
    case 'DRAFT':
      return 'Set race configuration and upload the athletes list, then continue.';
    case 'PACKAGING':
      if (model.readinessLoading) return 'Checking readiness…';
      if (isReadinessFetchError(model.readinessEntry)) return 'Readiness check failed — review and retry.';
      return 'Scan & assign devices and complete device commands, then continue.';
    case 'DISTRIBUTION':
      if (isCombinedModeRace(model.race)) {
        return 'Scan packets out at the desk; rack & verify in parallel.';
      }
      return 'Hand out athlete envelopes; scan back any undistributed devices.';
    case 'RACKING':
      return model.isRackingComplete
        ? 'Racking complete — continue to Active.'
        : `Rack and verify devices (${model.rackingRackedDevices}/${model.rackingActiveDevices} racked).`;
    case 'ACTIVE':
      return 'Race is live. Track retrieval below.';
    case 'REPORTING':
      return 'Generate race reports, then complete the race.';
    default:
      return 'Use the stage controls to move this race forward.';
  }
}

export function RaceDetailPortal(props: Props): ReactElement | null {
  const {
    open,
    model,
    eventId,
    isDetailsLoaded,
    isRefreshingEventDetails,
    statusReloadKey,
    isAdmin,
    globalUserIdToDisplayName,
    onClose,
    onRefresh,
  } = props;

  const [assignPortalOpen, setAssignPortalOpen] = useState(false);
  const [retrievalSectionOpen, setRetrievalSectionOpen] = useState(true);
  const [assignmentsPairingOpen, setAssignmentsPairingOpen] = useState(true);
  const [forwardGateState, setForwardGateState] = useState<'idle' | 'loading' | 'pass' | 'fail'>('idle');
  const [packagingCommandChecklist, setPackagingCommandChecklist] = useState(emptyPackagingCommandChecklist);
  const [deviceCommandsGateOpen, setDeviceCommandsGateOpen] = useState(false);
  const forwardGateResetRef = useRef<number | null>(null);
  const packagingPrepRef = useRef<HTMLDivElement | null>(null);
  const stackedOverlayWasOpenRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !isRaceStackedOverlayOpen()) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      stackedOverlayWasOpenRef.current = false;
      return;
    }
    const syncStackedOverlay = (): void => {
      const openNow = isRaceStackedOverlayOpen();
      if (stackedOverlayWasOpenRef.current && !openNow) {
        markRaceStackedOverlayJustClosed();
      }
      stackedOverlayWasOpenRef.current = openNow;
    };
    syncStackedOverlay();
    const observer = new MutationObserver(syncStackedOverlay);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open) setAssignPortalOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open || !model) return;
    const s = String(model.status ?? '').toUpperCase();
    setRetrievalSectionOpen(s !== 'RACKING');
    // The device/athlete pairing table is redundant with the Distribution/Racking
    // master table (same devices, same athletes) — start minimised there so the
    // master table is the primary view; still expandable for reference.
    setAssignmentsPairingOpen(s !== 'DISTRIBUTION' && s !== 'RACKING');
  }, [open, model?.raceId, model?.status]);

  useEffect(() => {
    if (!open || !model || String(model.status ?? '').toUpperCase() !== 'PACKAGING') return;
    setPackagingCommandChecklist(readPackagingCommandChecklist(eventId, model.raceId));
    setDeviceCommandsGateOpen(false);
  }, [open, eventId, model?.raceId, model?.status]);

  useEffect(() => {
    if (!open) return;
    const onChecklistChanged = (): void => {
      if (!model || String(model.status ?? '').toUpperCase() !== 'PACKAGING') return;
      setPackagingCommandChecklist(readPackagingCommandChecklist(eventId, model.raceId));
    };
    window.addEventListener('fleet-packaging-command-checklist-changed', onChecklistChanged);
    return () => window.removeEventListener('fleet-packaging-command-checklist-changed', onChecklistChanged);
  }, [open, eventId, model?.raceId, model?.status]);

  const handleTogglePackagingCommand = useCallback(
    (key: PackagingCommandStepKey) => {
      if (!model) return;
      const next = togglePackagingCommandStep(packagingCommandChecklist, key);
      setPackagingCommandChecklist(next);
      writePackagingCommandChecklist(eventId, model.raceId, next);
      window.dispatchEvent(new CustomEvent('fleet-packaging-command-checklist-changed'));
    },
    [eventId, model, packagingCommandChecklist],
  );

  useEffect(() => {
    if (forwardGateResetRef.current) {
      window.clearTimeout(forwardGateResetRef.current);
      forwardGateResetRef.current = null;
    }
    return () => {
      if (forwardGateResetRef.current) {
        window.clearTimeout(forwardGateResetRef.current);
      }
    };
  }, []);

  const overviewStatPills = useMemo((): DeviceStatPill[] => {
    if (!model) return [];
    const raceStatus = model.status;
    const pill = (
      key: Parameters<typeof isRaceOverviewStatRelevant>[0],
      label: string,
      value: string,
      icon: DeviceStatPill['icon'],
    ): DeviceStatPill => {
      const relevant = isRaceOverviewStatRelevant(key, raceStatus);
      return {
        label,
        icon,
        value: relevant ? value : '—',
        muted: !relevant,
        title: relevant ? undefined : raceOverviewStatUnavailableHint(key, raceStatus),
      };
    };

    return [
      pill(
        'athletes',
        'Athletes',
        model.athletesCountLoading ? '…' : String(model.athletesCount ?? 0),
        'heard',
      ),
      pill('athletePairs', 'Athlete pairs', String(model.athleteDevicePairs), 'race'),
      pill('assignedDevices', 'Assigned devices', String(model.assignedDevices), 'firmware'),
      pill(
        'unretrieved',
        'Unretrieved',
        model.devicesPendingRetrievalLoading
          ? '…'
          : model.unretrievedDevices != null
            ? String(model.unretrievedDevices)
            : '—',
        'debug',
      ),
      pill('retrieved', 'Retrieved', String(model.retrievedDevices), 'provisioning'),
    ];
  }, [model]);

  const athleteRoster = useAthleteRosterCard({
    eventId,
    raceId: model?.raceId ?? '',
    canEdit: isAdmin && !!model && !model.groupReadOnly && model.status === 'DRAFT',
    hasAthletesRoster: !!model?.hasAthletesRoster,
    pulseEdit: model?.status === 'DRAFT' && !model?.hasAthletesRoster,
    showReadyBadge: model?.status === 'DRAFT',
    showReadOnlyView: model?.status === 'DRAFT',
    onSaved: onRefresh,
  });

  if (!open || !model) return null;

  const { race, raceId, status } = model;
  const raceName = race.name?.trim() || raceId;
  const totalAssigned = countAssignedRaceDevices(model.displayRows);
  const totalRetrieved = countRetrievedRaceDevices(model.displayRows);
  const confirmedAthleteDevicePairs = countCompleteRaceAthleteDevicePairs(model.displayRows);
  const totalAthletesForPairing =
    model.athletesCount != null && model.athletesCount > 0 ? model.athletesCount : model.athleteDevicePairs;
  const assignmentsPairingComplete =
    totalAthletesForPairing > 0 && confirmedAthleteDevicePairs >= totalAthletesForPairing;

  const actionMap = new Map<string, RaceDetailActionItem>();
  for (const group of model.actionGroups) {
    for (const item of group.items) actionMap.set(item.id, item);
  }

  const backwardAction = actionMap.get('backward');
  const forwardAction = actionMap.get('forward-packaging') ?? actionMap.get('forward-next');
  const assignmentsAction = actionMap.get('packaging');
  const validationAction = actionMap.get('validation');
  const viewAllDevicesAction = actionMap.get('all-devices');
  const viewUnretrievedAction = actionMap.get('unretrieved-devices');
  const stageRetrievalAction = actionMap.get('stage-retrieval');
  const recallAction = actionMap.get('recall');
  const softDeleteAction = actionMap.get('soft-delete');
  const hardDeleteAction = actionMap.get('hard-delete');

  const showRetrieval = raceStatusShowsRetrieval(status);
  const retrievalTracking = showRetrieval && model.unretrievedDevices != null;
  const readinessOk =
    status === 'PACKAGING'
    && model.readinessEntry
    && !isReadinessFetchError(model.readinessEntry)
    && isPackagingToDistributionFullyReady(model.readinessEntry);
  const packagingCommandChecklistOk = areAllPackagingCommandStepsComplete(packagingCommandChecklist);
  const draftChecksOk =
    status === 'DRAFT'
    && model.raceCfgLoaded
    && model.hasAthletesRoster
    && model.raceCfgReady;
  const draftReadinessChecks: PackagingToDistributionReadinessCheck[] | null =
    status === 'DRAFT'
      ? [
        {
          key: 'athletes-list-uploaded',
          label: 'Uploaded athletes list',
          ok: model.hasAthletesRoster,
          detail: model.hasAthletesRoster
            ? 'Athletes CSV has been uploaded for this race.'
            : 'Upload athletes list before moving to Device Packaging.',
        },
        {
          key: 'race-configuration-set',
          label: 'Set race configuration',
          ok: model.raceCfgReady,
          detail: model.raceCfgReady
            ? 'Race configuration is set and ready for packaging.'
            : 'Set race configuration (firmware/version requirements) before moving forward.',
        },
      ]
      : null;
  const packagingReadinessChecks: PackagingToDistributionReadinessCheck[] | null =
    status === 'PACKAGING' && model.readinessEntry && !isReadinessFetchError(model.readinessEntry)
      ? model.readinessEntry
      : null;
  const packagingReadinessLoadError =
    status === 'PACKAGING' && model.readinessEntry && isReadinessFetchError(model.readinessEntry)
      ? model.readinessEntry.error
      : null;
  const pulseConfigCta = status === 'DRAFT' && !model.raceCfgReady;
  const pulseScanAssignCta = status === 'PACKAGING' && model.athleteDevicePairs < 1 && !assignPortalOpen;
  const pulseValidationCta = status === 'PACKAGING' && !model.validationReportViewed;
  const pulseDeviceCommandsCta = status === 'PACKAGING' && !packagingCommandChecklistOk;
  const packagingForwardPreReady =
    status === 'PACKAGING'
    && model.athleteDevicePairs >= 1
    && model.validationReportViewed;
  const pulseForwardCta =
    !!forwardAction
    && !model.progressing
    && !forwardAction.disabled
    && (
      (status === 'DRAFT' && draftChecksOk)
      || packagingForwardPreReady
      || status === 'DISTRIBUTION'
      || (status === 'RACKING' && model.isRackingComplete)
    );
  const packagingForwardReady =
    status === 'PACKAGING'
    && model.validationReportViewed
    && packagingCommandChecklistOk;
  const checksPassOk = status === 'DRAFT' ? draftChecksOk : status === 'PACKAGING' ? packagingForwardReady : readinessOk;
  const resetForwardGateStateSoon = (ms = 900): void => {
    if (forwardGateResetRef.current) window.clearTimeout(forwardGateResetRef.current);
    forwardGateResetRef.current = window.setTimeout(() => {
      setForwardGateState('idle');
      forwardGateResetRef.current = null;
    }, ms);
  };
  /**
   * Opens the validation report — but for PACKAGING races, every device-commands
   * checklist item must be confirmed first. Commands are what get devices ready to
   * pass verification, so this gate stops an operator from reading a report that's
   * guaranteed to show failures because the required commands were never sent.
   */
  const openValidationReportGated = (): void => {
    if (!validationAction) return;
    if (status === 'PACKAGING' && !packagingCommandChecklistOk) {
      setDeviceCommandsGateOpen(true);
      packagingPrepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    validationAction.onClick();
  };

  const handleForwardClick = (): void => {
    if (!forwardAction || model.progressing) return;
    setForwardGateState('loading');
    window.setTimeout(() => {
      const checksRequired = status === 'DRAFT' || status === 'PACKAGING';
      if (checksRequired && !checksPassOk) {
        setForwardGateState('fail');
        if (status === 'PACKAGING') {
          openValidationReportGated();
        }
        resetForwardGateStateSoon();
        return;
      }
      setForwardGateState('pass');
      forwardAction.onClick();
      resetForwardGateStateSoon();
    }, 240);
  };

  const readinessLabel = (() => {
    if (status === 'DRAFT') {
      if (!model.raceCfgLoaded) return 'Loading prerequisites';
      return model.hasAthletesRoster && model.raceCfgReady ? 'Ready for PACKAGING' : 'Needs config + athlete list';
    }
    if (status === 'PACKAGING') {
      if (model.readinessLoading) return 'Checking readiness';
      if (!model.readinessEntry) return 'No readiness data';
      if (isReadinessFetchError(model.readinessEntry)) return 'Readiness check failed';
      return readinessOk ? 'Ready for DISTRIBUTION' : 'Not ready for DISTRIBUTION';
    }
    if (status === 'RACKING') {
      return model.isRackingComplete
        ? `Ready for ACTIVE (${model.rackingRackedDevices}/${model.rackingActiveDevices} active devices racked)`
        : `Racking incomplete (${model.rackingRackedDevices}/${model.rackingActiveDevices} active devices racked)`;
    }
  })();
  const backwardTarget = stageTargetFromAction(backwardAction, 'backward');
  const forwardTarget = stageTargetFromAction(forwardAction, 'forward');
  const stageGuide = stageDescription(status, model);
  const hasAssignedDeviceRows = model.displayRows.length > 0;
  const validationReviewOutcome = getRaceValidationReviewOutcome({
    readinessEntry: model.readinessEntry,
    readinessLoading: model.readinessLoading,
  });
  const validationCtaTone: ValidationCtaTone = !model.validationReportViewed
    ? 'pending'
    : validationReviewOutcome === 'pass'
      ? 'pass'
      : validationReviewOutcome === 'fail'
        ? 'fail'
        : 'reviewed';
  const validationCtaLabel = !model.validationReportViewed
    ? 'Check validation report'
    : validationReviewOutcome === 'pass'
      ? 'Validation passed — view report'
      : validationReviewOutcome === 'fail'
        ? 'Validation failed, retry?'
        : 'Validation reviewed — view report';
  const statusBannerClass = stageStatusBannerClass(status);
  const statusEyebrow = stageStatusEyebrow(status);
  const combinedMode = isCombinedModeRace(race);

  const closeAssignPortal = (): void => {
    markRaceStackedOverlayJustClosed();
    window.setTimeout(() => setAssignPortalOpen(false), 0);
  };

  return (
    <>
      <PortaledLayer>
        <div
          className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/50 p-3 dark:bg-black/70 sm:p-5"
          role="dialog"
          aria-modal="true"
          aria-labelledby="race-detail-portal-title"
          onClick={(e) => {
            if (e.target !== e.currentTarget) return;
            if (isRaceStackedOverlayOpen() || shouldSuppressRacePortalBackdropClose()) return;
            onClose();
          }}
        >
          <div
            className="flex max-h-[min(92vh,920px)] w-full max-w-[min(1600px,96vw)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-800 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 overflow-hidden rounded-t-xl border-b border-gray-200 bg-gradient-to-r from-indigo-50 via-white to-emerald-50/80 px-4 py-3 dark:border-slate-700 dark:from-slate-800 dark:via-slate-900 dark:to-slate-950">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div id="race-detail-portal-title" className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 dark:text-slate-100">
                    <Flag className="h-4 w-4 shrink-0 text-indigo-600" />
                    <span className="truncate">{raceName}</span>
                    <span
                      className={[
                        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        getRaceStatusBadgeClass(status),
                      ].join(' ')}
                    >
                      {formatStatusLabel(status)}
                    </span>
                  </div>
                  {(race.wtProgramId || race.wtProgramName) ? (
                    <p className="mt-1 font-mono text-[11px] text-gray-600 dark:text-slate-300 break-all">
                      WT {String(race.wtProgramId ?? '').trim() || '—'}
                      {race.wtProgramName?.trim() ? ` · ${race.wtProgramName.trim()}` : ''}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  {softDeleteAction ? compactActionButton(softDeleteAction) : null}
                  {hardDeleteAction ? compactActionButton(hardDeleteAction) : null}
                  <RefreshButton
                    busy={isRefreshingEventDetails}
                    onClick={() => void onRefresh?.()}
                    disabled={!onRefresh}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:hover:bg-slate-800 disabled:opacity-50"
                    busyText="Refreshing…"
                  />
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
              <section className="sticky top-0 z-20 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50/95 to-white/95 px-4 py-3 shadow-sm backdrop-blur dark:border-slate-700 dark:from-slate-800/95 dark:to-slate-900/95">
                <div className="flex items-center justify-between gap-3">
                  {backwardAction ? (
                    <button
                      type="button"
                      disabled={backwardAction.disabled}
                      title={backwardAction.title ?? backwardAction.description}
                      className={stageNavButtonClass('backward')}
                      onClick={() => backwardAction.onClick()}
                    >
                      <ArrowLeft className="h-4 w-4" strokeWidth={2.8} />
                      <span className="hidden sm:inline">{backwardTarget || 'Back'}</span>
                    </button>
                  ) : (
                    <div className="h-10 w-10 shrink-0" aria-hidden="true" />
                  )}

                  <div className="min-w-0 flex-1 text-center">
                    <span
                      className={[
                        'inline-flex min-w-[9rem] flex-col items-center rounded-xl border px-4 py-1.5 text-center',
                        statusBannerClass,
                      ].join(' ')}
                    >
                      <span className="text-[9px] font-bold uppercase tracking-[0.18em] opacity-85">
                        {statusEyebrow}
                      </span>
                      <span className="mt-0.5 text-sm font-extrabold uppercase tracking-wide leading-none">
                        {formatStatusLabel(status)}
                      </span>
                    </span>
                    <p
                      className="mx-auto mt-1.5 max-w-xl truncate text-[11px] text-gray-600 dark:text-slate-300"
                      title={stageGuide}
                    >
                      {stageGuide}
                    </p>
                  </div>

                  {forwardAction ? (
                    <button
                      type="button"
                      disabled={
                        model.progressing
                        || forwardGateState === 'loading'
                        || forwardAction.disabled
                        || (status === 'RACKING' && !model.isRackingComplete)
                      }
                      title={forwardAction.title ?? forwardAction.description ?? readinessLabel}
                      className={stageNavButtonClass('forward', pulseForwardCta)}
                      onClick={handleForwardClick}
                    >
                      <span className="hidden sm:inline">{forwardTarget || 'Continue'}</span>
                      {forwardGateState === 'loading' ? (
                        <InlineSpinner size="sm" variant="current" />
                      ) : forwardGateState === 'fail' ? (
                        <CircleX className="h-4 w-4" />
                      ) : forwardGateState === 'pass' ? (
                        <CircleCheck className="h-4 w-4" />
                      ) : (
                        <ArrowRight className="h-4 w-4" strokeWidth={2.8} />
                      )}
                    </button>
                  ) : (
                    <div className="h-10 w-10 shrink-0" aria-hidden="true" />
                  )}
                </div>
              </section>

              <DeviceStatPills items={overviewStatPills} />

              {status === 'REPORTING' ? (
                <DetailCard title="Race reporting">
                  <RaceReportingPanel
                    eventId={eventId}
                    raceId={raceId}
                    statusReloadKey={statusReloadKey}
                  />
                </DetailCard>
              ) : (<>
              <div className="grid grid-cols-1 gap-4">
                <RaceConfigurationCard
                  eventId={eventId}
                  raceId={raceId}
                  canEdit={isAdmin && !model.groupReadOnly}
                  raceCfg={model.raceConfiguration}
                  raceCfgLoaded={model.raceCfgLoaded}
                  pulseEdit={pulseConfigCta}
                  showReadyBadge={status === 'DRAFT'}
                  raceCfgReady={model.raceCfgReady}
                  globalUserIdToDisplayName={globalUserIdToDisplayName}
                  onSaved={onRefresh}
                />
              </div>

              <DetailCard
                title={status === 'DRAFT' ? 'Athletes' : 'Athlete to device pairing'}
                right={
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {athleteRoster.headerRight}
                    {assignmentsAction ? (
                      <span
                        className={[
                          'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide tabular-nums',
                          assignmentsPairingComplete
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100'
                            : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100',
                        ].join(' ')}
                        title={`${confirmedAthleteDevicePairs}/${totalAthletesForPairing} athletes have a confirmed rear + front device pair`}
                      >
                        {assignmentsPairingComplete ? (
                          <CircleCheck className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
                        ) : null}
                        {confirmedAthleteDevicePairs}/{totalAthletesForPairing} confirmed
                      </span>
                    ) : null}
                    {validationAction && model.validationReportViewed ? (
                      <span
                        className={[
                          'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                          validationReviewOutcome === 'pass'
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100'
                            : validationReviewOutcome === 'fail'
                              ? 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-700/60 dark:bg-rose-950/40 dark:text-rose-100'
                              : 'border-gray-300 bg-gray-50 text-gray-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200',
                        ].join(' ')}
                        title={
                          validationReviewOutcome === 'pass'
                            ? 'Validation report: all checks passed'
                            : validationReviewOutcome === 'fail'
                              ? 'Validation report: failures found — open the report'
                              : 'Validation report reviewed'
                        }
                      >
                        {validationReviewOutcome === 'pass' ? (
                          <CircleCheck className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
                        ) : validationReviewOutcome === 'fail' ? (
                          <CircleX className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
                        ) : null}
                        {validationReviewOutcome === 'pass'
                          ? 'Validation passed'
                          : validationReviewOutcome === 'fail'
                            ? 'Validation failed'
                            : 'Validation reviewed'}
                      </span>
                    ) : null}
                    {assignmentsAction ? (
                      <button
                        type="button"
                        className={[
                          actionToneClass('primary'),
                          pulseScanAssignCta ? strongPulseClass() : '',
                        ].join(' ')}
                        onClick={() => setAssignPortalOpen(true)}
                      >
                        Edit assignments
                      </button>
                    ) : null}
                    {status !== 'PACKAGING' && validationAction ? compactActionButton(validationAction, undefined, { pulse: pulseValidationCta }) : null}
                    {viewAllDevicesAction ? compactActionButton(viewAllDevicesAction) : null}
                    {viewUnretrievedAction ? compactActionButton(viewUnretrievedAction, 'View unretrieved') : null}
                    {status === 'DISTRIBUTION' || status === 'RACKING' ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        onClick={() => setAssignmentsPairingOpen((prev) => !prev)}
                      >
                        {assignmentsPairingOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        {assignmentsPairingOpen ? 'Collapse' : 'Expand'}
                      </button>
                    ) : null}
                  </div>
                }
              >
                {status === 'DRAFT' && athleteRoster.body ? (
                  <div className="mb-3">{athleteRoster.body}</div>
                ) : null}

                {status !== 'DRAFT' ? (<>
                {model.fromAthleteCsvFallback ? (
                  <div className="mb-3 rounded-lg border border-violet-200 bg-violet-50/80 px-3 py-2 text-[11px] text-violet-900 dark:border-violet-800/60 dark:bg-violet-950/30 dark:text-violet-100">
                    Showing assignments from the latest athlete CSV (read-only snapshot).
                  </div>
                ) : null}


                {(status === 'DISTRIBUTION' || status === 'RACKING') && !assignmentsPairingOpen ? (
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    Expand to view the full pairing table — collection and racking status are tracked below.
                  </p>
                ) : !isDetailsLoaded ? (
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {isRefreshingEventDetails ? 'Loading event details…' : 'Event details not loaded yet.'}
                  </p>
                ) : model.loadingCsvFallback ? (
                  <p className="text-xs text-gray-600 dark:text-slate-400">
                    Checking latest athlete assignment file…
                  </p>
                ) : !hasAssignedDeviceRows ? (
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {status === 'DRAFT'
                      ? 'Move to Device Packaging, then assign devices.'
                      : 'Assign devices to athletes.'}
                  </p>
                ) : (
                  <div className="max-h-[min(55vh,560px)] overflow-auto rounded-lg border border-gray-200 dark:border-slate-700">
                    <EventAssignedDevicesTable
                      eventId={eventId}
                      devices={model.displayRows}
                      raceStatus={status}
                      statusReloadKey={statusReloadKey}
                      isAdmin={isAdmin}
                      isReadOnly={model.groupReadOnly || model.fromAthleteCsvFallback}
                      lockedHint={
                        model.fromAthleteCsvFallback
                          ? 'These rows come from the athlete assignment CSV; they are not editable here.'
                          : model.lockedHint
                      }
                      globalUserIdToDisplayName={globalUserIdToDisplayName}
                      selectedDeviceIds={[]}
                      setSelectedDeviceIds={() => { }}
                      onToggleDeviceSelected={() => { }}
                      onRefresh={onRefresh}
                    />
                  </div>
                )}
                </>) : null}
              </DetailCard>

              {status === 'DISTRIBUTION' || status === 'RACKING' ? (
                <DetailCard title="Collection & racking">
                  <DistributionMasterTable
                    eventId={eventId}
                    raceId={raceId}
                    raceStatus={status}
                    combinedMode={combinedMode}
                    devices={model.displayRows}
                    onRefresh={onRefresh}
                  />
                </DetailCard>
              ) : null}

              {status === 'PACKAGING' && validationAction ? (
                <div
                  ref={packagingPrepRef}
                  className={[
                    'rounded-xl border border-slate-200 p-4 dark:border-slate-700',
                    pulseDeviceCommandsCta
                      ? 'ring-2 ring-blue-500/70 ring-offset-2 ring-offset-white dark:ring-offset-slate-900'
                      : '',
                  ].join(' ')}
                >
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Verify devices</h3>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {packagingCommandChecklistOk
                      ? 'Every device command is confirmed — open the report to check.'
                      : 'Opens the device commands checklist first — confirm every command has been sent before the report can run.'}
                  </p>
                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={openValidationReportGated}
                      className={validationCtaButtonClass(validationCtaTone, pulseValidationCta)}
                    >
                      {validationCtaTone === 'pass' ? (
                        <CircleCheck className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                      ) : validationCtaTone === 'fail' ? (
                        <CircleX className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                      ) : null}
                      {validationCtaLabel}
                    </button>
                  </div>
                </div>
              ) : null}

              {showRetrieval ? (
                <DetailCard
                  title="Retrieval"
                  right={
                    <div className="flex flex-wrap justify-end gap-2">
                      {recallAction ? compactActionButton(recallAction) : null}
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        onClick={() => setRetrievalSectionOpen((prev) => !prev)}
                      >
                        {retrievalSectionOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        {retrievalSectionOpen ? 'Collapse' : 'Expand'}
                      </button>
                    </div>
                  }
                >
                  {!retrievalSectionOpen ? (
                    <p className="text-xs text-gray-500 dark:text-slate-400">Expand to view retrieval progress.</p>
                  ) : totalAssigned === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-slate-400">Assign devices to track retrieval.</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-800">
                        <div
                          className="h-full bg-gray-400 transition-all duration-700 ease-out dark:bg-slate-500"
                          style={{ width: `${totalAssigned > 0 ? (totalRetrieved / totalAssigned) * 100 : 0}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 dark:text-slate-300">
                        {totalRetrieved} of {totalAssigned} devices retrieved
                        {model.unretrievedDevices != null ? ` · ${model.unretrievedDevices} pending` : ''}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400">
                        Distribution {model.retrievedByPhase.distribution} · Racking {model.retrievedByPhase.racking} · Active {model.retrievedByPhase.active}
                        {retrievalTracking ? ` · Unretrieved ${model.unretrievedDevices ?? 0}` : ''}
                      </p>
                    </div>
                  )}

                  {retrievalSectionOpen && model.renderStagedRetrieval ? (
                    <div className="mt-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                      {model.renderStagedRetrieval()}
                    </div>
                  ) : retrievalSectionOpen && stageRetrievalAction && !model.renderStagedRetrieval ? (
                    <div className="mt-3">
                      {compactActionButton(stageRetrievalAction, 'Activate scanner')}
                    </div>
                  ) : null}
                </DetailCard>
              ) : null}
              </>)}
            </div>
          </div>
        </div>
      </PortaledLayer>

      {assignmentsAction ? (
        <DevicePackageAssignmentPortal
          open={assignPortalOpen}
          eventId={eventId}
          raceId={raceId}
          raceName={raceName}
          assignmentsLocked={model.groupReadOnly}
          onClose={closeAssignPortal}
          onSaved={() => { void onRefresh?.(); }}
        />
      ) : null}

      {status === 'PACKAGING' ? (
        <DeviceCommandsGateDialog
          open={deviceCommandsGateOpen}
          raceConfiguration={model.raceConfiguration}
          checklist={packagingCommandChecklist}
          onToggleStep={handleTogglePackagingCommand}
          onClose={() => setDeviceCommandsGateOpen(false)}
          onContinue={() => {
            setDeviceCommandsGateOpen(false);
            validationAction?.onClick();
          }}
        />
      ) : null}
    </>
  );
}

